import { buildMapsCategoria } from '../../../core/catalog/categoriasProducto.js';

import { escapeHtml as esc } from '../../../core/ui/escapeHtml.js';
import { MLV_Z } from '../../../core/ui/mlvZLayers.js';

const { ETIQUETA_CATEGORIA } = buildMapsCategoria();

function formatPrecioAr(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return '—';
  return x.toLocaleString('es-AR', { maximumFractionDigits: 0 });
}

function iconClose() {
  return `<svg class="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>`;
}

function slideSectionHtml(p) {
  const fotos = Array.isArray(p.fotos) ? p.fotos.filter(Boolean) : [];
  const urls = fotos.length ? fotos : [''];
  const gallery = urls
    .map((url) => {
      const inner = url
        ? `<img src="${esc(url)}" alt="" class="h-full w-full object-contain" loading="lazy" />`
        : `<div class="flex h-full w-full items-center justify-center text-sm text-white/40">Sin foto</div>`;
      return `<div class="feed-gal-item flex h-full min-w-full shrink-0 snap-center items-center justify-center bg-black">${inner}</div>`;
    })
    .join('');

  return `
    <section class="feed-slide relative flex min-h-[100dvh] w-full shrink-0 snap-start flex-col bg-black" data-product-id="${esc(p.id)}">
      <div class="relative flex min-h-0 flex-1 flex-col pt-12">
        <div class="feed-gallery flex max-h-[72vh] min-h-[52vh] flex-1 overflow-x-auto overflow-y-hidden snap-x snap-mandatory [-webkit-overflow-scrolling:touch]">
          ${gallery}
        </div>
        <div class="pointer-events-none absolute bottom-28 left-0 right-0 px-4">
          <p class="text-lg font-bold leading-snug text-white drop-shadow-md">${esc(p.titulo || 'Sin título')}</p>
          <p class="mt-1 text-2xl font-bold text-brand-secondary drop-shadow-md">$ ${esc(formatPrecioAr(p.precio))}</p>
        </div>
      </div>
      <div class="pointer-events-auto absolute bottom-0 left-0 right-0 z-10 flex flex-col gap-2 px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-2">
        <button type="button" class="feed-btn-desc w-full rounded-full bg-white py-3.5 text-sm font-semibold text-slate-900 shadow-lg">
          Ver descripción y comprar
        </button>
        <p class="pb-1 text-center text-[10px] leading-snug text-white/45">Deslizá ↑ para más en esta categoría · deslizá ↔ para ver las fotos</p>
      </div>
    </section>
  `;
}

/**
 * Feed vertical estilo “shorts”: un producto por pantalla, misma categoría.
 */
export function createProductoFeedVerticalPresenter({ render, catalogoRepository }) {
  async function showFeed({
    categoriaId,
    startProductId,
    isAuthenticated = false,
    onBack,
    onVerDescripcion,
  }) {
    render(`
      <div class="fixed inset-0 flex items-center justify-center bg-black" style="z-index:${MLV_Z.feedFullscreen}">
        <p class="text-sm text-white/80">Cargando experiencia…</p>
      </div>
    `);

    let first;
    try {
      first = await catalogoRepository.obtenerProductoPorId(startProductId);
    } catch (e) {
      console.error(e);
      render(`
        <div class="min-h-screen bg-brand-surface px-4 py-10 text-center">
          <p class="text-sm text-red-600">No se pudo abrir el feed.</p>
          <button type="button" id="feed-err-back" class="mt-4 rounded-full bg-brand-primary px-5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-brand-primary/90">Volver</button>
        </div>
      `);
      document.getElementById('feed-err-back').onclick = () => onBack?.();
      return;
    }

    if (!first) {
      render(`
        <div class="min-h-screen bg-brand-surface px-4 py-10 text-center">
          <p class="text-sm text-slate-600">No encontramos esta publicación.</p>
          <button type="button" id="feed-err-back" class="mt-4 rounded-full bg-brand-primary px-5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-brand-primary/90">Volver</button>
        </div>
      `);
      document.getElementById('feed-err-back').onclick = () => onBack?.();
      return;
    }

    if (!isAuthenticated && first.estado !== 'publicado') {
      render(`
        <div class="min-h-screen bg-brand-surface px-4 py-10 text-center">
          <p class="text-sm text-slate-600">Esta publicación no está disponible.</p>
          <button type="button" id="feed-err-back" class="mt-4 rounded-full bg-brand-primary px-5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-brand-primary/90">Volver</button>
        </div>
      `);
      document.getElementById('feed-err-back').onclick = () => onBack?.();
      return;
    }

    const catNorm = first.categoria_id || 'general';
    const catLabel = String(ETIQUETA_CATEGORIA[catNorm] || categoriaId || catNorm);

    const slides = [first];
    const seen = new Set([first.id]);
    let loadingMore = false;
    let hasMore = true;
    let sentinelText = 'Cargando más…';

    function bindSlideButtons(root = document) {
      root.querySelectorAll('.feed-slide .feed-btn-desc').forEach((btn) => {
        if (btn.dataset.feedBound === '1') return;
        btn.dataset.feedBound = '1';
        btn.onclick = (e) => {
          e.stopPropagation();
          const slide = btn.closest('.feed-slide');
          const id = slide?.getAttribute('data-product-id');
          if (id) onVerDescripcion?.(id);
        };
      });
    }

    function renderShell(slidesHtml) {
      render(`
        <div id="feed-root" class="fixed inset-0 flex flex-col bg-black" style="z-index:${MLV_Z.feedFullscreen}">
          <header class="absolute left-0 right-0 top-0 z-20 flex items-center justify-between px-2 py-2 pt-[max(0.5rem,env(safe-area-inset-top))]">
            <button type="button" id="feed-close" class="flex h-11 w-11 items-center justify-center rounded-full text-white/95 hover:bg-white/10" aria-label="Cerrar">
              ${iconClose()}
            </button>
            <span class="max-w-[65%] truncate text-center text-[11px] font-semibold uppercase tracking-wide text-white/85">${esc(catLabel)}</span>
            <span class="w-11" aria-hidden="true"></span>
          </header>
          <div id="feed-scroll" class="feed-vscroll flex-1 overflow-y-auto overflow-x-hidden snap-y snap-mandatory scroll-smooth">
            ${slidesHtml}
            <div id="feed-sentinel" class="flex min-h-[28vh] w-full shrink-0 snap-start flex-col items-center justify-center px-6 pb-8 pt-4">
              <span class="feed-sentinel-msg text-center text-xs text-white/50">${esc(sentinelText)}</span>
            </div>
          </div>
        </div>
      `);
      document.getElementById('feed-close').onclick = () => onBack?.();
      bindSlideButtons();
    }

    function appendSlidesHtml(htmlChunk) {
      const sentinel = document.getElementById('feed-sentinel');
      if (!sentinel) return;
      sentinel.insertAdjacentHTML('beforebegin', htmlChunk);
      bindSlideButtons(sentinel.parentElement);
    }

    function updateSentinelMsg(text) {
      sentinelText = text;
      const el = document.querySelector('.feed-sentinel-msg');
      if (el) el.textContent = text;
    }

    function wireScrollLoad() {
      const feedScroll = document.getElementById('feed-scroll');
      if (!feedScroll) return;
      const onScroll = () => {
        const { scrollTop, clientHeight, scrollHeight } = feedScroll;
        if (scrollHeight - scrollTop - clientHeight < 480) void tryLoadMore();
      };
      feedScroll.addEventListener('scroll', onScroll, { passive: true });
    }

    async function tryLoadMore() {
      if (loadingMore || !hasMore) return;
      const last = slides[slides.length - 1];
      if (!last) return;
      loadingMore = true;
      updateSentinelMsg('Cargando más…');
      try {
        const { items, hasMore: more } =
          await catalogoRepository.listarProductosFeedCategoriaSiguientes({
            categoriaId: catNorm,
            afterProductId: last.id,
            pageSize: 8,
          });
        hasMore = more;
        const nuevos = [];
        for (const it of items) {
          if (!seen.has(it.id)) {
            seen.add(it.id);
            slides.push(it);
            nuevos.push(it);
          }
        }
        if (nuevos.length) {
          appendSlidesHtml(nuevos.map((p) => slideSectionHtml(p)).join(''));
        }
        if (!hasMore) updateSentinelMsg('Llegaste al final de esta categoría');
        else updateSentinelMsg('Deslizá hacia arriba para seguir explorando');
      } catch (err) {
        console.error(err);
        hasMore = false;
        updateSentinelMsg(
          err?.code === 'failed-precondition'
            ? 'Falta un índice en Firestore (estado + categoria_id + id). Desplegá índices o usá el enlace del error en consola.'
            : 'No se pudieron cargar más publicaciones.'
        );
      } finally {
        loadingMore = false;
      }
    }

    renderShell(slideSectionHtml(first));
    wireScrollLoad();
    await tryLoadMore();
  }

  return { showFeed };
}
